/**
 * REV-796: keep <html lang> and <html dir> in step with the language the page
 * is actually RENDERED in.
 *
 * `lang` is what makes a screen reader switch pronunciation (WCAG 3.1.1), so it
 * must describe the text on the page. That is `resolvedLanguage` (the language a
 * translation was found in), NOT `language` (what the browser detector asked
 * for). A French browser today yields language=fr, resolvedLanguage=en: the page
 * is English and must say so. When German resources ship (REV-801) a German user
 * resolves to `de` and `lang` follows with no code change.
 *
 * Formatting locale (dates, numbers) deliberately stays on `i18n.language`, see
 * src/utils/format.ts.
 */
import type { i18n as I18n } from 'i18next';

type Minimal = Pick<I18n, 'language' | 'resolvedLanguage' | 'dir' | 'options'>;

/** The language the page text is in; English if nothing has resolved yet. */
export function renderedLanguage(instance: Minimal): string {
  const fallback = instance.options?.fallbackLng;
  const fallbackLng = Array.isArray(fallback) ? fallback[0] : fallback;
  return instance.resolvedLanguage || (typeof fallbackLng === 'string' ? fallbackLng : 'en');
}

export function syncDocumentLanguage(instance: Minimal) {
  if (typeof document === 'undefined') return;
  const lng = renderedLanguage(instance);
  document.documentElement.lang = lng;
  document.documentElement.dir = instance.dir(lng);
}

/** Sync now and on every subsequent language change. Returns an unsubscribe. */
export function bindDocumentLanguage(instance: I18n): () => void {
  const handler = () => syncDocumentLanguage(instance);
  instance.on('languageChanged', handler);
  handler();
  return () => instance.off('languageChanged', handler);
}
